import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { postSupportMessage, postSupportMessageMedia } from '../../api/support_chat_api';
import { getSupportChatErrorMessage, validateAttachment } from './utils';

/**
 * Поле ввода: текст, одно вложение и отправка
 * (только текст, только файл или файл с подписью).
 *
 * Сообщение сразу появляется в ленте как pending, не дожидаясь
 * ответа сервера и следующего поллинга.
 */
export function useSupportChatComposer({ onSent, addPendingMessage, removePendingMessage }) {
  const [text, setText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  const openFilePicker = () => fileInputRef.current?.click();

  const handleFilePick = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const validationError = validateAttachment(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSelectedFile(file);
  };

  const removeSelectedFile = () => setSelectedFile(null);

  const send = async () => {
    if (sending) return;

    const caption = text.trim();
    if (!selectedFile && !caption) {
      toast.error('Заполните сообщение или прикрепите файл');
      return;
    }

    const pendingId = addPendingMessage({
      sender_role: 'user',
      body: caption,
      created_at: new Date().toISOString(),
      pendingFile: selectedFile || undefined,
    });

    setSending(true);
    try {
      if (selectedFile) {
        await postSupportMessageMedia(selectedFile, caption || undefined);
        toast.success('Вложение отправлено');
      } else {
        await postSupportMessage(caption);
        toast.success('Сообщение отправлено');
      }

      setText('');
      setSelectedFile(null);
      await onSent?.();
    } catch (error) {
      toast.error(getSupportChatErrorMessage(error));
    } finally {
      removePendingMessage(pendingId);
      setSending(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    if (event.key !== 'Enter' && event.code !== 'Enter' && event.code !== 'NumpadEnter') return;
    if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) return;

    event.preventDefault();
    void send();
  };

  return {
    text,
    setText,
    selectedFile,
    sending,
    fileInputRef,
    canSend: !sending && (Boolean(selectedFile) || Boolean(text.trim())),
    openFilePicker,
    handleFilePick,
    removeSelectedFile,
    handleKeyDown,
    send,
  };
}
